// src/systems/combat/engine.ts — Motor de combate estratégico 1v1 (Fase 4)
// Separa estado, cálculo, clima/terreno/estado/habilidad/IA del UI.

import type { AsteriaSpecies, AsteriaInstance } from '../../entities/asteria.js';
import type { AsteriaMove } from '../../entities/asteria.js';
import { getMove } from '../../data/moves.js';
import { getSpecies } from '../../data/asterias.js';
import { calcStats } from '../../entities/asteria.js';
import { getAbility } from '../../data/abilities.js';
import { getStatus } from '../../data/status.js';
import { getWeather } from '../../data/weather.js';
import { getTerrain } from '../../data/terrain.js';
import type { TrainerDef } from '../../data/trainers.js';
import { calculateDamage, getStageMultiplier, xpRewardForVictory, aureosReward, xpToNext } from './calculator.js';
import { affinityMultiplier } from '../../data/affinities.js';

export type BattleUnit = {
  species: AsteriaSpecies;
  instance?: AsteriaInstance; // si es del jugador
  level: number;
  hp: number; maxHp: number;
  energy: number; maxEnergy: number;
  atk: number; def: number; spd: number;
  iv: number;
  abilityId: string;
  moves: AsteriaMove[];
  statStages: { atk:number; def:number; spd:number; acc:number };
  status: { id:string; turns:number } | null;
  name: string;
  isPlayer: boolean;
  // para trainer: índice en equipo
  teamIndex?: number;
};

export type BattleField = {
  weather: { id:string; turns:number };
  terrain: { id:string; turns:number };
};

export type BattleLogEntry = { text:string; kind?: 'info'|'damage'|'heal'|'status'|'crit' };

export type Battle = {
  id: string;
  isTrainer: boolean;
  trainer?: TrainerDef;
  playerTeam: BattleUnit[];
  enemyTeam: BattleUnit[];
  playerActive: number;
  enemyActive: number;
  field: BattleField;
  turn: number;
  log: BattleLogEntry[];
  result: 'ongoing'|'victory'|'defeat'|'fled';
  captureAllowed: boolean;
  // para XP: quién participó
  participants: Set<string>; // uids de player
};

export function createBattleUnitFromInstance(inst: AsteriaInstance, isPlayer:boolean): BattleUnit {
  const sp = getSpecies(inst.speciesId);
  if(!sp) throw new Error(`Species ${inst.speciesId} not found`);
  const moves = inst.moves.map(id=> getMove(id)).filter(Boolean) as AsteriaMove[];
  // si no tiene moves (viejo save), usa los de la especie
  const finalMoves = moves.length? moves : sp.moves.slice(0,4);
  return {
    species: sp,
    instance: inst,
    level: inst.level,
    hp: inst.hp, maxHp: inst.maxHp,
    energy: inst.energy, maxEnergy: inst.maxEnergy,
    atk: inst.atk, def: inst.def, spd: inst.spd,
    iv: inst.iv,
    abilityId: inst.abilityId,
    moves: finalMoves,
    statStages: { ...inst.statStages },
    status: inst.status ? { ...inst.status } : null,
    name: inst.nickname ?? sp.name,
    isPlayer,
  };
}

export function createWildBattleUnit(speciesId:string, level:number, iv=15): BattleUnit {
  const sp = getSpecies(speciesId);
  if(!sp) throw new Error(`Species ${speciesId} not found`);
  const st = calcStats(sp, level, iv);
  const moves = sp.moves.slice(0,4);
  return {
    species: sp,
    level,
    hp: st.hp, maxHp: st.maxHp,
    energy: st.maxEnergy, maxEnergy: st.maxEnergy,
    atk: st.atk, def: st.def, spd: st.spd,
    iv,
    abilityId: sp.abilityId,
    moves,
    statStages: { atk:0, def:0, spd:0, acc:0 },
    status: null,
    name: sp.name,
    isPlayer:false,
  };
}

export function createBattle(opts:{
  playerTeam: AsteriaInstance[];
  wildSpeciesId?: string;
  wildLevel?: number;
  trainer?: TrainerDef;
  weatherId?: string;
  terrainId?: string;
}): Battle {
  const playerUnits = opts.playerTeam
    .filter(inst=> inst.hp > 0)
    .map(inst=> createBattleUnitFromInstance(inst, true));
  // si no tiene equipo, usa proxy explorador (Fase 3 compat)
  let effectivePlayerTeam = playerUnits;
  if(effectivePlayerTeam.length===0){
    // crea un "explorador" temporal con Verdora prestada
    const sp = getSpecies('verdora')!;
    effectivePlayerTeam = [{
      species: sp, level:5, hp:45, maxHp:45, energy:10, maxEnergy:10,
      atk:42, def:38, spd:50, iv:15, abilityId:'fotosintesis',
      moves: [getMove('golpe_vinculo')!, getMove('pulso_suave')!],
      statStages:{atk:0,def:0,spd:0,acc:0}, status:null, name:'Explorador', isPlayer:true
    }];
  }

  let enemyTeam: BattleUnit[] = [];
  let isTrainer = false;
  let trainer: TrainerDef | undefined;
  if(opts.trainer){
    isTrainer = true; trainer = opts.trainer;
    enemyTeam = trainer.team.map((m,i)=> {
      const u = createWildBattleUnit(m.speciesId, m.level);
      (u as any).teamIndex = i;
      return u;
    });
  } else if(opts.wildSpeciesId){
    enemyTeam = [createWildBattleUnit(opts.wildSpeciesId, opts.wildLevel ?? 5)];
  } else {
    throw new Error('createBattle needs wildSpeciesId or trainer');
  }

  const battle: Battle = {
    id: `b_${Date.now()}`,
    isTrainer, trainer,
    playerTeam: effectivePlayerTeam,
    enemyTeam,
    playerActive: 0,
    enemyActive: 0,
    field: {
      weather: { id: opts.weatherId ?? 'despejado', turns: opts.weatherId && opts.weatherId!=='despejado' ? 4 : 99 },
      terrain: { id: opts.terrainId ?? 'normal', turns: opts.terrainId && opts.terrainId!=='normal' ? 4 : 99 },
    },
    turn: 1,
    log: [],
    result: 'ongoing',
    captureAllowed: !isTrainer,
    participants: new Set(effectivePlayerTeam.filter(u=>u.instance).map(u=> u.instance!.uid)),
  };
  battle.log.push({ text: isTrainer ? `¡${trainer!.name} te desafía!` : `¡Un ${enemyTeam[0].species.name} salvaje apareció!` });
  // mensaje clima/terreno inicial si no es despejado
  return battle;
}

export function getActivePlayer(b:Battle): BattleUnit { return b.playerTeam[b.playerActive]; }
export function getActiveEnemy(b:Battle): BattleUnit { return b.enemyTeam[b.enemyActive]; }

export function canSwitch(b:Battle, idx:number): boolean {
  if(idx <0 || idx>= b.playerTeam.length) return false;
  if(idx === b.playerActive) return false;
  const u = b.playerTeam[idx];
  if(u.hp <=0) return false;
  // enraizado bloquea cambio
  const active = getActivePlayer(b);
  if(active.status?.id === 'enraizado') return false;
  return true;
}

// Orden de turno: prioridad > velocidad con modificadores
export function determineOrder(b:Battle, playerMove?: AsteriaMove, enemyMove?: AsteriaMove): ('player'|'enemy')[] {
  const p = getActivePlayer(b), e = getActiveEnemy(b);
  const pPrio = playerMove?.priority ?? 0;
  const ePrio = enemyMove?.priority ?? 0;
  if(pPrio !== ePrio) return pPrio > ePrio ? ['player','enemy'] : ['enemy','player'];
  const pSpd = p.spd * getStageMultiplier(p.statStages.spd) * (p.status?.id==='ralentizado'?0.7:1);
  const eSpd = e.spd * getStageMultiplier(e.statStages.spd) * (e.status?.id==='ralentizado'?0.7:1);
  // habilidad ráfaga etc ya aplicado en pSpd via ability? simplificado
  if(pSpd === eSpd) return Math.random()<0.5 ? ['player','enemy'] : ['enemy','player'];
  return pSpd > eSpd ? ['player','enemy'] : ['enemy','player'];
}

export type TurnAction =
  | { type:'move'; moveId:string }
  | { type:'switch'; index:number }
  | { type:'item'; itemId:string }
  | { type:'capture' }
  | { type:'flee' };

export function executeTurn(b:Battle, playerAction: TurnAction, enemyMoveId?: string): Battle {
  if(b.result!=='ongoing') return b;
  const playerUnit = getActivePlayer(b);
  const enemyUnit = getActiveEnemy(b);

  // Resolver enemy move: IA si no se pasa
  let enemyAction: TurnAction;
  if(enemyMoveId){
    enemyAction = { type:'move', moveId: enemyMoveId };
  } else {
    enemyAction = { type:'move', moveId: enemyUnit.moves[Math.floor(Math.random()* enemyUnit.moves.length)]?.id ?? enemyUnit.moves[0].id };
  }

  // Casos especiales que no entran en orden: switch, item, capture, flee tienen prioridad fuera de daño?
  // Simplificación: switch/item/capture/flee se resuelven antes del ataque enemigo si el jugador los elige, y el enemigo no ataca ese turno (salvo si huye falla)
  if(playerAction.type === 'switch'){
    if(!canSwitch(b, playerAction.index)){
      b.log.push({ text:'¡No puedes cambiar a esa Asteria!' });
      return b;
    }
    b.log.push({ text: `${playerUnit.name} vuelve. ¡Ve, ${b.playerTeam[playerAction.index].name}!` });
    b.playerActive = playerAction.index;
    b.participants.add(b.playerTeam[b.playerActive].instance?.uid ?? '');
    // enemigo ataca después del cambio
    const enemyMove = getMove((enemyAction as any).moveId);
    if(enemyMove){
      applyMove(b, enemyUnit, getActivePlayer(b), enemyMove);
    }
    endTurn(b);
    return b;
  }
  if(playerAction.type === 'item'){
    const healed = handleItem(b, playerAction.itemId);
    b.log.push({ text: healed.message });
    // enemigo ataca
    const enemyMove = getMove((enemyAction as any).moveId);
    if(enemyMove) applyMove(b, enemyUnit, getActivePlayer(b), enemyMove);
    endTurn(b);
    return b;
  }
  if(playerAction.type === 'capture'){
    // solo salvaje
    if(!b.captureAllowed){
      b.log.push({ text:'¡No puedes capturar el equipo de un entrenador!' });
      return b;
    }
    // no consume turno enemigo, captura se resuelve aparte (llamado desde UI)
    // aquí solo log
    b.log.push({ text: `${playerUnit.name} lanza un Núcleo...` });
    return b;
  }
  if(playerAction.type === 'flee'){
    const fled = attemptFlee(b);
    if(fled){
      b.result='fled';
      b.log.push({ text:'¡Huiste con éxito!' });
    } else {
      b.log.push({ text:'¡No pudiste huir!' });
      const enemyMove = getMove((enemyAction as any).moveId);
      if(enemyMove) applyMove(b, enemyUnit, playerUnit, enemyMove);
      endTurn(b);
    }
    return b;
  }

  // Ambos son moves: determinar orden
  const playerMove = getMove((playerAction as any).moveId);
  const enemyMove = getMove((enemyAction as any).moveId);
  if(!playerMove || !enemyMove){
    b.log.push({ text:'Movimiento no encontrado' });
    return b;
  }

  const order = determineOrder(b, playerMove, enemyMove);
  for(const side of order){
    if(b.result!=='ongoing') break;
    const attacker = side==='player'? getActivePlayer(b) : getActiveEnemy(b);
    const defender = side==='player'? getActiveEnemy(b) : getActivePlayer(b);
    const move = side==='player'? playerMove : enemyMove;
    // verificar energía
    if(attacker.energy < move.energyCost){
      b.log.push({ text:`${attacker.name} está agotado y no puede usar ${move.name}.` });
      // pierde turno pero no hace nada
      continue;
    }
    attacker.energy = Math.max(0, attacker.energy - move.energyCost);
    applyMove(b, attacker, defender, move);
    // si defensor fainted, manejar
    if(defender.hp <=0){
      defender.hp = 0;
      b.log.push({ text:`¡${defender.name} debilitado!` });
      if(defender.isPlayer){
        // buscar siguiente vivo
        const next = b.playerTeam.findIndex((u,i)=> i!== b.playerActive && u.hp>0);
        if(next===-1){
          b.result='defeat';
          b.log.push({ text:'¡Todo tu equipo cayó!' });
        } else {
          b.log.push({ text:`¡${defender.name} fuera! Elige siguiente.` });
          // no auto-switch, UI pedirá switch
        }
      } else {
        // enemigo
        if(b.isTrainer){
          const nextEnemy = b.enemyTeam.findIndex((u,i)=> i!== b.enemyActive && u.hp>0);
          if(nextEnemy===-1){
            b.result='victory';
            b.log.push({ text:`¡Has vencido a ${b.trainer?.name}!` });
          } else {
            b.enemyActive = nextEnemy;
            b.log.push({ text:`¡${b.trainer?.name} envía a ${getActiveEnemy(b).name}!` });
          }
        } else {
          b.result='victory';
          b.log.push({ text:`¡Venciste al ${defender.name} salvaje!` });
        }
      }
      if(b.result!=='ongoing') break;
    }
  }

  if(b.result==='ongoing') endTurn(b);
  return b;
}

function applyMove(b:Battle, attacker:BattleUnit, defender:BattleUnit, move: AsteriaMove): void {
  // precisión con estados/clima
  let acc = move.accuracy;
  const atkAccStage = getStageMultiplier(attacker.statStages.acc);
  const defAccStage = getStageMultiplier(defender.statStages.acc);
  acc *= atkAccStage / defAccStage;
  // niebla reduce
  if(b.field.weather.id==='neblina') acc *= 0.85;
  if(b.field.weather.id==='tormenta') acc *= 0.90;
  // interferencia
  if(attacker.status?.id==='interferencia') acc *= 0.80;
  if(attacker.status?.id==='confusion' && Math.random()<0.30){
    b.log.push({ text:`${attacker.name} está confundido y se hace daño.` , kind:'status' });
    attacker.hp = Math.max(0, attacker.hp - Math.floor(attacker.maxHp*0.08));
    return;
  }
  if(Math.random() > acc){
    b.log.push({ text:`${attacker.name} usó ${move.name} ¡Falló!` });
    return;
  }

  // efectos de control que no hacen daño
  if(move.category==='control' || move.category==='defensa' || move.category==='estado'){
    handleControlMove(b, attacker, defender, move);
    return;
  }

  // ataque
  const dmgRes = calculateDamage(attacker, defender, move, b.field);
  defender.hp = Math.max(0, defender.hp - dmgRes.damage);
  let msg = `${attacker.name} usó ${move.name} → -${dmgRes.damage} PS`;
  if(dmgRes.affinityLabel) msg += ` ${dmgRes.affinityLabel}`;
  if(dmgRes.isCrit) msg += ' ¡Crítico!';
  b.log.push({ text: msg, kind: dmgRes.isCrit? 'crit':'damage' });

  // efecto secundario de estado
  if(move.effect?.statusId && move.effect.chance && Math.random() < move.effect.chance){
    const statusDef = getStatus(move.effect.statusId);
    if(statusDef){
      defender.status = { id: statusDef.id, turns: statusDef.duration };
      b.log.push({ text:`${defender.name} quedó ${statusDef.name}.`, kind:'status' });
    }
  }
  // si es ataque con terreno/clima set? no, control ya maneja
}

function handleControlMove(b:Battle, attacker:BattleUnit, defender:BattleUnit, move: AsteriaMove): void {
  if(move.effect?.weatherId){
    b.field.weather = { id: move.effect.weatherId, turns: 4 };
    b.log.push({ text:`¡El clima cambió a ${getWeather(move.effect.weatherId)?.name}!` });
    return;
  }
  if(move.effect?.terrainId){
    b.field.terrain = { id: move.effect.terrainId, turns: 4 };
    b.log.push({ text:`¡Terreno ahora es ${getTerrain(move.effect.terrainId)?.name}!` });
    return;
  }
  if(move.effect?.selfBuff){
    const buff = move.effect.selfBuff;
    if(buff==='defUp'){
      attacker.statStages.def = Math.min(6, attacker.statStages.def +1);
      b.log.push({ text:`${attacker.name} subió su defensa.` });
    } else if(buff==='atkUp'){
      attacker.statStages.atk = Math.min(6, attacker.statStages.atk +1);
      b.log.push({ text:`${attacker.name} subió su ataque.` });
    } else if(buff==='defDown'){
      defender.statStages.def = Math.max(-6, defender.statStages.def -1);
      b.log.push({ text:`${defender.name} bajó su defensa.` });
    }
    return;
  }
  if(move.effect?.statusId){
    const chance = move.effect.chance ?? 1;
    if(Math.random() < chance){
      const def = getStatus(move.effect.statusId);
      if(def){
        defender.status = { id: def.id, turns: def.duration };
        b.log.push({ text:`${defender.name} ahora está ${def.name}.`, kind:'status' });
      }
    } else {
      b.log.push({ text:`${attacker.name} usó ${move.name} ¡Sin efecto!` });
    }
    return;
  }
  // por defecto si no tiene efecto definido
  b.log.push({ text:`${attacker.name} usó ${move.name}.` });
}

function handleItem(b:Battle, itemId:string): { message:string } {
  const active = getActivePlayer(b);
  // pocion_aurea cura 35 PS
  if(itemId==='pocion_aurea'){
    const before = active.hp;
    active.hp = Math.min(active.maxHp, active.hp + 35);
    // consumir del inventario se hace fuera (store), aquí solo efecto
    return { message:`${active.name} recuperó ${active.hp-before} PS.` };
  }
  if(itemId==='cristal_lumen'){
    const before = active.energy;
    active.energy = Math.min(active.maxEnergy, active.energy + 3);
    return { message:`${active.name} recuperó ${active.energy-before} energía.` };
  }
  return { message:`Usaste ${itemId}.` };
}

export function attemptFlee(b:Battle): boolean {
  const p = getActivePlayer(b), e = getActiveEnemy(b);
  const pSpd = p.spd * getStageMultiplier(p.statStages.spd);
  const eSpd = e.spd * getStageMultiplier(e.statStages.spd);
  const diff = (pSpd - eSpd)/100;
  const chance = Math.max(0.35, Math.min(0.92, b.isTrainer? 0.15 : 0.65 + diff));
  return Math.random() < chance;
}

function endTurn(b:Battle): void {
  b.turn++;
  // clima cuenta
  if(b.field.weather.turns < 99){
    b.field.weather.turns--;
    if(b.field.weather.turns<=0){
      b.log.push({ text:`El clima ${b.field.weather.id} se disipó.` });
      b.field.weather = { id:'despejado', turns:99 };
    } else if(b.field.weather.id==='lluvia'){
      // cura leve aqua
      const p = getActivePlayer(b), e = getActiveEnemy(b);
      if(p.species.element==='aqua' && p.hp>0){
        const heal = Math.floor(p.maxHp*0.04);
        p.hp = Math.min(p.maxHp, p.hp+heal);
        b.log.push({ text:`Lluvia cura a ${p.name} +${heal} PS.`, kind:'heal' });
      }
      if(e.species.element==='aqua' && e.hp>0){
        const heal = Math.floor(e.maxHp*0.04);
        e.hp = Math.min(e.maxHp, e.hp+heal);
      }
    }
  }
  if(b.field.terrain.turns < 99){
    b.field.terrain.turns--;
    if(b.field.terrain.turns<=0){
      b.log.push({ text:`El terreno ${b.field.terrain.id} volvió a normal.` });
      b.field.terrain = { id:'normal', turns:99 };
    } else if(b.field.terrain.id==='vegetal'){
      const p=getActivePlayer(b), e=getActiveEnemy(b);
      if(p.species.element==='terra' && p.hp>0){
        const heal=Math.floor(p.maxHp*0.06);
        p.hp=Math.min(p.maxHp, p.hp+heal);
        b.log.push({ text:`Terreno vegetal cura a ${p.name} +${heal}`, kind:'heal' });
      }
    }
  }
  // estados
  for(const u of [getActivePlayer(b), getActiveEnemy(b)]){
    if(u.status){
      const def = getStatus(u.status.id);
      if(def?.onTurn){
        const res = def.onTurn(u);
        if(res.damage){
          if(res.damage<0){
            u.hp = Math.min(u.maxHp, u.hp - res.damage); // res.damage negativo cura
          } else {
            u.hp = Math.max(0, u.hp - res.damage);
            b.log.push({ text:`${u.name} sufre ${def.name} -${res.damage}`, kind:'damage' });
          }
        }
        if(res.message) b.log.push({ text: res.message });
        if(res.skip){
          b.log.push({ text:`${u.name} no puede actuar por ${def.name}` });
        }
      }
      if(def?.modifiers){
        // ya aplicado vía getStageMultiplier? No, status modifiers se aplican en cálculo
      }
      u.status.turns--;
      if(u.status.turns<=0){
        b.log.push({ text:`${u.name} ya no está ${def?.name}.` });
        u.status=null;
      }
    }
    // habilidad pasiva por turno
    const abil = getAbility(u.abilityId);
    if(abil){
      const ctx:any = { owner: u.isPlayer?'player':'enemy', weather: b.field.weather.id, terrain: b.field.terrain.id };
      const eff = abil.apply(u, ctx);
      if(eff?.heal){
        u.hp = Math.min(u.maxHp, u.hp + eff.heal);
        b.log.push({ text:`${u.species.name} [${abil.name}] cura +${eff.heal}`, kind:'heal' });
      }
    }
    // regeneración leve de energía
    u.energy = Math.min(u.maxEnergy, u.energy +1);
  }
}

// Recompensas
export function applyVictoryRewards(b:Battle, playerState:any): { xpGained:number; leveled: string[]; aureos:number; items:any[] } {
  const enemy = b.enemyTeam[b.enemyActive];
  // usamos el primer enemigo para XP simple, si trainer promedia niveles
  const avgLevel = b.enemyTeam.reduce((s,u)=>s+u.level,0)/b.enemyTeam.length;
  const xp = xpRewardForVictory(Math.round(avgLevel), b.isTrainer);
  const aureos = aureosReward(Math.round(avgLevel), enemy.species.rarity, b.isTrainer);
  const leveled: string[] = [];
  // repartir XP a todos los participantes vivos
  for(const uid of b.participants){
    const inst = [...playerState.team.asterias, ...playerState.team.boxed].find((i:any)=> i.uid===uid);
    if(!inst || inst.hp<=0) continue;
    inst.xp += xp;
    // subir nivel mientras alcance
    while(inst.xp >= inst.xpToNext && inst.level < 50){
      inst.xp -= inst.xpToNext;
      inst.level++;
      inst.xpToNext = xpToNext(inst.level);
      // recalcular stats
      const sp = getSpecies(inst.speciesId)!;
      const st = calcStats(sp, inst.level, inst.iv);
      inst.maxHp = st.maxHp;
      inst.atk = st.atk; inst.def = st.def; inst.spd = st.spd;
      inst.maxEnergy = st.maxEnergy;
      inst.hp = Math.min(inst.hp+ Math.floor(st.maxHp*0.15), st.maxHp); // cura al subir
      inst.energy = st.maxEnergy;
      inst.bond = Math.min(100, inst.bond + 4);
      leveled.push(`${sp.name} Nv.${inst.level}`);
      // aprender nuevo movimiento si tiene?
    }
    if(inst.bond !==undefined) inst.bond = Math.min(100, inst.bond + 2);
  }
  playerState.currency = (playerState.currency ?? 0) + aureos;
  if(b.isTrainer && b.trainer?.reward.items){
    for(const it of b.trainer.reward.items){
      const inv = playerState.inventory.find((i:any)=> i.id===it.id);
      if(inv) inv.qty += it.qty; else playerState.inventory.push({ ...it });
    }
  }
  playerState.stats.battlesWon = (playerState.stats.battlesWon ?? 0) +1;
  return { xpGained: xp, leveled, aureos, items: b.trainer?.reward.items ?? [] };
}

export function syncBattleToInstances(b:Battle, playerState:any): void {
  // sincronizar HP/energía/estado de vuelta a las instancias
  for(const unit of b.playerTeam){
    if(!unit.instance) continue;
    const inst = [...playerState.team.asterias, ...playerState.team.boxed].find((i:any)=> i.uid===unit.instance!.uid);
    if(inst){
      inst.hp = unit.hp;
      inst.energy = unit.energy;
      inst.statStages = { ...unit.statStages };
      inst.status = unit.status ? { ...unit.status } : null;
      // si fainted, hp 0
    }
  }
}
